import React, { useState, useEffect, memo } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import {
  favouriteIcon,
  filledHeartIcon,
  Icon,
  LoadingIndicator,
} from "@lululemon/ecom-pattern-library";
import { useToast } from "@lululemon/ui-library";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { log } from "@lululemon/mwa-utils";
import { isUserLoggedIn } from "@lululemon/mwa-auth";
import { getRedirectLoginReturnUrl } from "@lululemon/ecom-pdp/src/utils/url";
import styles from "./wishlist-heart-container-cdp.module.scss";
import { WISHLIST_HEART_KEY } from "../../utils/constants";
import { checkItemInWishlists, stashItem, popItem } from "../../utils/helpers";
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics"; // needs mwa-analytics-next later
import { useWishlistInterface } from "../../interface/use-wishlist-interface";
import {
  prepareToRemoveFromWishlists,
  getProductDetails,
  addSkuToWishlist,
  addStyleOptionToWishlist,
} from "./wishlist-heart-container-cdp.actions";
import { renderWishlistCTA } from "@lululemon/ecom-wishlist/src/components/wishlist-cta/wishlist-cta";
import { useMicrocopyJSON } from "@lululemon/ecom-common/src/components/use-microcopy/use-microcopy";
import { WishlistContentfulContextProvider as MessagesProvider } from "../../contexts/wishlist-context";
import { useV3SigninModal } from "@lululemon/ecom-common/src/hooks/useV3SigninModal";
import { getParsedLocale } from "@lululemon/ecom-common/src/util/locale-utils";
import { ErrorBoundary } from "@lululemon/mwa-logger";
import { savedItemsReportError } from "../../logger/saved-items-logger";

const ToastMessage = memo(function ToastMessage({
  skuNotFound,
  addToWishlistError,
  wishlistItem,
  isMobile,
  messages,
}) {
  const { added, isOos } = wishlistItem || {};

  let toastHeader = null;
  let toastBody = null;
  let toastDescription = null;

  // Determine toastHeader
  // Wishlist item add
  if (added) {
    toastHeader =
      messages?.["ge.wishlist.cdp.toast.added.onelist"] || "Item saved";
  } else if (!added && !skuNotFound && !addToWishlistError) {
    toastHeader =
      messages?.["ge.wishlist.cdp.toast.removed.onelist"] || "Item removed";
  }

  // Determine toastBody
  if (skuNotFound) {
    toastBody =
      messages?.["ge.wishlist.cdp.toast.skuNotFound"] ||
      "Darn, this one is not available. Please select another color/size.";
  } else if (addToWishlistError) {
    toastBody =
      messages?.["ge.wishlist.cdp.toast.error"] ||
      "Something went wrong. Please try again.";
  }

  // Determine toastDescription
  if (!isMobile && isOos) {
    toastDescription =
      messages?.["ge.wishlist.cdp.toast.oos"] ||
      "We can’t guarantee this item will be restocked.";
  }

  return (
    <div data-testid="wishlist-toast" className={styles.toastMessage}>
      {toastHeader && (
        <div className="lll-text-body-1 lll-font-weight-medium">
          {toastHeader}
        </div>
      )}
      {toastBody && <span className="lll-text-body-1">{toastBody}</span>}
      {toastDescription && (
        <div className="lll-text-body-3">{toastDescription}</div>
      )}
    </div>
  );
});

const getAttributesDetails = ({ isSerp }) => {
  if (isSerp) {
    return "list-serp";
  } else {
    return "list-cdp";
  }
};

const WishlistHeart = ({
  isItemInWishlist,
  isMobile,
  graphqlClient,
  product,
  selectedSwatchIndex,
  swatches,
  wishlists,
  setIsItemInWishlist,
  isSerp,
  productIndex,
  productCount,
}) => {
  const { fetchWishlists, addToWishlist, removeFromWishlists } =
    useWishlistInterface();

  const [hovered, setHovered] = useState(false);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  // initial value = all-available-sizes in case api fetch for sizes fails
  const [sizes, setSizes] = useState(product?.["all-available-sizes"] || []);
  const [showSpinner, setShowSpinner] = useState(false);
  const [wishlistItem, setWishlistItem] = useState({
    added: false,
    isOos: false,
  });
  const { skus: skuCache = {}, sizeGuides: sizeGuidesCache = {} } = useSelector(
    (state) => state?.rootReducer
  );
  const [skuNotFound, setSkuNotFound] = useState(false);
  const [addToWishlistError, setAddToWishlistError] = useState(false);
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const isGuestLoggedIn = isUserLoggedIn();
  const [filledHeart, setFilledHeart] = useState(
    hovered || wishlistItem.added || isItemInWishlist
  );

  const [skusToDelete, setSkusToDelete] = useState({});
  const [bvRedirectUrl, setBvRedirectUrl] = useState("");
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [productId, setProductId] = useState(
    product?.["product-id"] || product?.["repository-id"]
  );
  const [sizeGuide, setSizeGuide] = useState({});
  const productName = {
    unified: "N/A",
    localized:
      product?.["display-name"] ||
      product?.["parent-category-display-name"] ||
      "",
  };
  const unifiedId = product?.["unified-id"] || "";
  const categoryUnifiedId = product?.["parent-category-unified-id"] || "";
  const listPrice = product?.["list-price"]?.[0] || "";
  const salePrice = product?.["product-sale-price"]?.[0] || "";
  const onSale = product?.["product-on-sale"];
  const collectionType = "ProductList";
  const componentType = filledHeart ? "filledHeartIcon" : "favouriteIcon";
  const color = swatches?.[selectedSwatchIndex]?.id;

  const styleIds = product?.["sku-style-order"]?.find(
    (style) => style?.["color-id"] === color
  );
  const styleOption = styleIds?.["style-id"];
  const styleId1 = styleIds?.["style-id-01"];
  const styleId2 = styleIds?.["style-id-02"];
  const { locale } = useRouter();
  const [shouldTriggerToast, setShouldTriggerToast] = useState(false);

  useEffect(() => {
    if (!isGuestLoggedIn) return;

    const { hasItem } = checkItemInWishlists({
      wishlists,
      styleOption,
      productId,
      color,
    });
    setFilledHeart(hasItem || hovered); // need this to flicker the heart
    setIsItemInWishlist(hasItem); // need this to sync when moving around products, cats
  }, [selectedSwatchIndex, swatches, hovered, wishlists, isGuestLoggedIn]);

  // TODO: plug in analytics tracking from another ticket
  useEffect(() => {
    if (shouldTriggerToast) {
      showToast({
        message: (
          <ToastMessage
            skuNotFound={skuNotFound}
            addToWishlistError={addToWishlistError}
            wishlistItem={wishlistItem}
            isMobile={isMobile}
            messages={contentfulMessages}
          />
        ),
        ...(wishlistItem && {
          action: renderWishlistCTA({
            wishlists,
            messages: contentfulMessages,
          }),
        }),
        className: styles.toast,
      });
      setWishlistItem({ added: false, isOos: false });
      setSkuNotFound(false);
      setAddToWishlistError(false);
      setShouldTriggerToast(false);
    }
  }, [
    shouldTriggerToast,
    wishlistItem,
    skuNotFound,
    addToWishlistError,
    renderWishlistCTA,
    wishlists,
  ]);

  const contentfulMessages = useMicrocopyJSON(
    "gated-experiences.cdp.wishlist.json"
  );

  const sendAnalyticsEvent = ({ componentId, skuList }) => {
    // tracking 1 sku only
    trackEvent(EVENT_CATEGORY.PRODUCT_INTERACTION, {
      component: {
        id: componentId,
        type: componentType,
        text: "",
        position: productIndex,
        totalCount: productCount,
        attributes: {
          details: getAttributesDetails({ isSerp }),
        },
      },
      collectionList: [
        {
          id: componentId,
          type: collectionType,
          productList: [
            {
              name: productName,
              productId,
              skuList,
              unifiedId,
              categoryUnifiedId,
            },
          ],
        },
      ],
    })?.catch((warning) => log.warning(warning));
  };

  // adds a sku item to the default wishlist
  const addSkuToDefaultWishlist = async ({
    selectedSku,
    selectedSize,
    wishlists,
  }) => {
    try {
      const componentId = "list-add";
      // tracking 1 sku only
      sendAnalyticsEvent({
        componentId,
        skuList: [
          {
            attributes: {
              styleId1,
              styleId2,
            },
            price: {
              onSale,
              displaySale: salePrice,
              displayRegular: listPrice,
            },
            sku: selectedSku?.id,
            // selectedSize = fallback, un matched againt cache
            size: selectedSku?.size || selectedSize,
          },
        ],
      });

      const wasItemAdded = await addToWishlist({
        wishlistId: wishlists?.[0]?.id || "default",
        id: selectedSku?.id,
        wishlistItemDetails: {
          styleOption,
          productId,
          inseam: selectedSku?.inseam,
          size: selectedSku?.size,
          color: selectedSku?.color?.code,
          type: "sku",
          countryCode: getParsedLocale()?.countryCode,
        },
      });
      setWishlistItem({
        added: wasItemAdded,
        isOos: !selectedSku?.available,
      });
      setFilledHeart(wasItemAdded);
    } catch (error) {
      log.error(WISHLIST_HEART_KEY, error);
      setAddToWishlistError(true);
    }
  };

  // adds a sku-less item to the default wishlist
  const addSkuLessToDefaultWishlist = async () => {
    const wasItemAdded = await addStyleOptionToWishlist({
      sendAnalyticsEvent,
      productStyles: product?.["sku-style-order"],
      addToWishlist,
      productId,
      product,
      color,
      countryCode: getParsedLocale()?.countryCode,
    });
    if (wasItemAdded) {
      setWishlistItem({
        added: wasItemAdded,
        isOos: false,
      });
      setFilledHeart(wasItemAdded);
    } else {
      setAddToWishlistError(true);
    }
    setShouldTriggerToast(true);
  };

  const prepareToAddToWishlist = async ({ wishlists, selectedSize, skus }) => {
    // find sku from cache
    const selectedSku = [
      ...(skuCache?.[`${productId}`] ?? []),
      ...(skus ?? []),
    ].find(
      ({ color, size }) =>
        color?.code?.toString() ===
          swatches?.[selectedSwatchIndex]?.id?.toString() &&
        size?.toString() === selectedSize?.toString()
    );

    // find sku from first api fetch when it failed to save to cache
    if (selectedSku?.id) {
      await addSkuToDefaultWishlist({ selectedSku, selectedSize, wishlists });
    } else {
      setSkuNotFound(true);
    }
    setShouldTriggerToast(true);
  };

  const removeSkus = async (skus) => {
    setShowSpinner(true);
    try {
      const componentId = "list-remove";
      // tracking potentially multiple skus
      const skuList = [];
      Object.values(skus).forEach((value) => {
        value.forEach((item) => {
          skuList.push({
            attributes: {
              styleId1,
              styleId2,
            },
            price: {
              onSale,
              displaySale: salePrice,
              displayRegular: listPrice,
            },
            sku: item?.sku || "",
            size: item?.size || "",
          });
        });
      });

      sendAnalyticsEvent({ componentId, skuList });
      const removed = await removeFromWishlists({ skus });
      // change solid heart to empty if all successful
      if (!removed) {
        throw new Error("not all skus were removed!");
      }
      setWishlistItem({
        isOos: false,
        added: false,
      });
      // reset for the hover state
      setFilledHeart(false);
    } catch (error) {
      log.error("wishlist remove sku error", error);
      setAddToWishlistError(true);
    } finally {
      setShouldTriggerToast(true);
      setShowSpinner(false);
      setSkusToDelete({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isItemInWishlist) {
      prepareToRemoveFromWishlists({
        productId,
        wishlists,
        swatches,
        selectedSwatchIndex,
        setSkusToDelete,
        removeSkus,
      });
      return;
    }

    setSkuNotFound(false);
    setAddToWishlistError(false);

    if (!isGuestLoggedIn) {
      stashItem({
        productId,
        swatches,
        selectedSwatchIndex,
      });
      setBvRedirectUrl(getRedirectLoginReturnUrl(window?.location?.href));
      setIsLoginModalOpen(true);
      return;
    }

    setShowSizeSelector(false);
    if (Object.keys(sizeGuidesCache).includes(productId)) {
      // getting size guide from cache
      const sizeGuide = sizeGuidesCache[productId];
      setSizeGuide(sizeGuide);
    }

    await addSkuLessToDefaultWishlist();
  };

  const handleLoginModalSuccess = async () => {
    const { wishlists } = await fetchWishlists({
      createDefaultIfMissing: true,
    });

    try {
      const storedProduct = popItem();
      if (storedProduct) {
        const { productId, color } = storedProduct;
        const { hasItem, inDefaultWishlist, sku } = checkItemInWishlists({
          wishlists,
          productId,
          color,
        });

        if (!hasItem || (hasItem && !inDefaultWishlist)) {
          await addSkuLessToDefaultWishlist();
          return;
        }

        if (!hasItem) {
          setProductId(productId);
          // Start add to wishlist flow.
          const { skus: productSkus } = await getProductDetails({
            product,
            productId,
            graphqlClient,
            dispatch,
            color,
            unifiedId,
            category: categoryUnifiedId,
            setSizeGuide,
            locale,
          });

          addSkuToWishlist({
            allSkus: productSkus,
            swatches,
            setSizes,
            setShowSizeSelector,
            prepareToAddToWishlist,
            wishlists,
          });
        }

        if (hasItem && !inDefaultWishlist) {
          setProductId(productId);
          // add to default wishlist, bypass sku retrieval from cache or api
          await prepareToAddToWishlist({
            wishlists,
            selectedSku: sku,
          });
        }
      }
    } catch (error) {
      log.error(error);
    }
  };

  const modalSource = getAttributesDetails({
    isSerp,
    defaultKey: WISHLIST_HEART_KEY,
  });

  useV3SigninModal({
    onLoginSuccess: handleLoginModalSuccess,
    source: modalSource,
    mode: WISHLIST_HEART_KEY,
    isLoginModalOpen,
    onClose: () => setIsLoginModalOpen(false),
  });

  return (
    <ErrorBoundary
      onError={(error) =>
        savedItemsReportError(error, "WishlistHeartContainerCDP", "Saved Items")
      }
    >
      <MessagesProvider translations={contentfulMessages}>
        <button
          aria-label={
            filledHeart
              ? contentfulMessages?.["ge.wishlist.cdp.a11y.remove"] ||
                "Remove from wish list"
              : contentfulMessages?.["ge.wishlist.cdp.a11y.add"] ||
                "Add to wish list"
          }
          type="button"
          data-testid="wishlist-heart"
          onClick={handleSubmit}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onTouchStart={() => {
            setTimeout(() => {
              setHovered(true);
            }, 200);

            setTimeout(() => {
              setHovered(false);
            }, 300);
          }}
          className={classnames({
            [styles.emptyHeartIcon]: !filledHeart,
            [styles.filledHeartIcon]: filledHeart,
          })}
        >
          <Icon content={filledHeart ? filledHeartIcon : favouriteIcon} />
        </button>
        {showSpinner && (
          <LoadingIndicator className={styles.loadingIndicator} color="gray" />
        )}
      </MessagesProvider>
    </ErrorBoundary>
  );
};

WishlistHeart.propTypes = {
  isItemInWishlist: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  product: PropTypes.object.isRequired,
  graphqlClient: PropTypes.shape({
    query: PropTypes.func.isRequired,
    mutation: PropTypes.func.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  swatches: PropTypes.arrayOf({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  wishlists: PropTypes.arrayOf(PropTypes.object),
  setIsItemInWishlist: PropTypes.func.isRequired,
  productIndex: PropTypes.number.isRequired,
  productCount: PropTypes.number.isRequired,
};

WishlistHeart.defaulProps = {
  isItemInWishlist: false,
  isMobile: false,
  product: {},
  graphqlClient: {},
  swatches: [],
  wishlists: [],
  setIsItemInWishlist: () => {},
  productIndex: 0,
  productCount: 0, // -1 from analytics if not available, but this is always available
};

export default WishlistHeart;
